/**
 * La carte affichée dans la fenêtre : fond, régions, sites, mailles, point saisi ;
 * recherche de site et saisie de coordonnées. Elle ne connaît ni le champ ni la
 * fenêtre : elle signale une sélection par `surSelection`.
 */
import OlMap from 'ol/Map'
import View from 'ol/View'
import Feature from 'ol/Feature'
import Overlay from 'ol/Overlay'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import GeoJSON from 'ol/format/GeoJSON'
import Point from 'ol/geom/Point'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import { fromLonLat, transformExtent } from 'ol/proj'
import { defaults as defaultControls, ScaleLine } from 'ol/control'
import type { FeatureLike } from 'ol/Feature'
import type MapBrowserEvent from 'ol/MapBrowserEvent'
import type Geometry from 'ol/geom/Geometry'

import { formaterDecimal, lireCoordonnees, type Emprise } from './coordonnees'
import { lireJson, versMaille, type EntiteGrille, type SourceGrille } from './donnees'
import { PROP_TYPE, type ChargeurSites } from './chargeurSites'
import { filtrerSites, type FiltreSites } from './filtreSites'
import type { Texteur } from './textes'
import type { FondDeCarte, Maille, SourceSelection, TypeDeSite } from './types'

/** Tuiles gardées en mémoire ; au-delà, les plus anciennes quittent la carte. */
const TUILES_MAX = 30
/** Marge de chargement autour de la vue, en degrés. */
const MARGE_VUE = 0.1
const ZOOM_SELECTION = 11
const PALETTE = ['#15803d', '#65a30d', '#0284c7', '#0891b2', '#9333ea', '#c2410c', '#be185d', '#4d7c0f']

export interface OptionsCarte {
  source: SourceGrille
  regions?: string
  /** Couches de sites (`couches.sites`), `null` si aucune. */
  sites: ChargeurSites | null
  champs: { siteType: string | null; nom: string; code: string }
  fond: FondDeCarte | false
  zoomMailles: number
  hauteur: string
  lectureSeule: boolean
  typesSite: Record<string, TypeDeSite>
  t: Texteur
  surSelection: (m: Maille | null) => void
  surErreur: (e: Error) => void
}

/** Couleur avec transparence : accepte #rgb, #rrggbb ; toute autre notation est rendue telle quelle, avec un repli. */
function translucide(couleur: string, alpha: number, repli: string): string {
  let c = couleur.trim()
  if (/^#[0-9a-f]{3}$/i.test(c)) c = '#' + [...c.slice(1)].map((x) => x + x).join('')
  if (/^#[0-9a-f]{6}$/i.test(c)) return c + Math.round(alpha * 255).toString(16).padStart(2, '0')
  return repli
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, classe: string, texte?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (classe) e.className = classe
  if (texte !== undefined) e.textContent = texte
  return e
}

function bouton(classe: string, texte: string): HTMLButtonElement {
  const b = el('button', classe, texte)
  b.type = 'button'   // dans un <form>, un bouton sans type soumet le formulaire
  return b
}

export class Carte {
  private readonly o: OptionsCarte
  private readonly t: Texteur
  private map!: OlMap
  private readonly mailles = new VectorSource()
  private readonly sites = new VectorSource()
  private readonly regions = new VectorSource()
  private readonly point = new VectorSource()
  private readonly chargees = new Map<string, Feature[]>()
  private readonly enCours = new Map<string, Promise<void>>()
  private readonly format = new GeoJSON({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
  private selection: Maille | null = null
  /** Vrai tant que ni l'utilisateur ni une sélection n'ont déplacé la vue initiale. */
  private cadrageAuto = true
  private survolId: number | null = null
  private styles!: { maille: Style; avecSite: Style; survol: Style; selection: Style; region: Style; point: Style }
  private readonly stylesSite = new Map<string, Style>()
  private couleursSite: Record<string, string> = {}
  /** Types des couches chargées ; vide tant qu'elles ne le sont pas. `null` : aucune couche, aucun filtre. */
  private filtre: FiltreSites

  private readonly elInfo: HTMLDivElement
  private readonly elAide: HTMLDivElement
  private readonly elBulle: HTMLDivElement
  private readonly elMessage: HTMLDivElement
  private readonly elCoord: HTMLInputElement
  private readonly elEffacer: HTMLButtonElement
  private readonly elLegende: HTMLDivElement
  private readonly elListe: HTMLDataListElement
  private readonly elRecherche: HTMLInputElement
  private observateur: ResizeObserver | null = null

  constructor(readonly racine: HTMLElement, options: OptionsCarte) {
    this.o = options
    this.t = options.t
    this.filtre = options.sites ? new Set<string>() : null
    racine.classList.add('sgi-carte')
    racine.setAttribute('role', 'group')
    racine.setAttribute('aria-label', this.t('groupeAria'))

    // ── Barre ─────────────────────────────────────────────────────────────────
    const barre = el('div', 'sgi-barre')
    this.elRecherche = el('input', 'sgi-saisie sgi-recherche')
    this.elRecherche.type = 'search'
    this.elRecherche.placeholder = this.t('rechercheSite')
    this.elRecherche.setAttribute('aria-label', this.t('rechercheAria'))
    this.elListe = el('datalist', '')
    this.elListe.id = `sgi-sites-${Math.random().toString(36).slice(2, 8)}`
    this.elRecherche.setAttribute('list', this.elListe.id)
    this.elRecherche.hidden = !options.sites

    const groupe = el('div', 'sgi-coord')
    this.elCoord = el('input', 'sgi-saisie sgi-coord-champ')
    this.elCoord.type = 'text'
    this.elCoord.autocomplete = 'off'
    this.elCoord.spellcheck = false
    const [o, sud, e, nord] = options.source.emprise
    const exemple = `${((sud + nord) / 2).toFixed(4)}, ${((o + e) / 2).toFixed(4)}`
    this.elCoord.placeholder = this.t('coordonnees', { exemple })
    this.elCoord.title = this.t('coordonneesAide', { exemple })
    this.elCoord.setAttribute('aria-label', this.t('coordonneesAria'))
    const localiser = bouton('sgi-bouton', this.t('localiser'))
    groupe.append(this.elCoord, localiser)
    this.elEffacer = bouton('sgi-bouton', this.t('effacer'))
    this.elEffacer.hidden = options.lectureSeule
    this.elEffacer.disabled = true
    barre.append(this.elRecherche, this.elListe, groupe, this.elEffacer)

    this.elMessage = el('div', 'sgi-message')
    this.elMessage.setAttribute('aria-live', 'polite')
    this.elMessage.hidden = true

    const cadre = el('div', 'sgi-cadre-carte')
    cadre.style.height = options.hauteur
    this.elAide = el('div', 'sgi-aide')
    this.elAide.hidden = true
    this.elBulle = el('div', 'sgi-bulle')
    this.elBulle.hidden = true
    cadre.append(this.elAide)

    this.elInfo = el('div', 'sgi-info')
    this.elInfo.setAttribute('aria-live', 'polite')
    this.elLegende = el('div', 'sgi-legende')
    racine.replaceChildren(barre, this.elMessage, cadre, this.elInfo, this.elLegende)

    this.construireStyles()
    this.creerCarte(cadre)
    this.afficherInfo()

    this.elRecherche.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.allerAuSite(this.elRecherche.value) }
    })
    this.elRecherche.addEventListener('change', () => this.allerAuSite(this.elRecherche.value))
    const lancer = () => void this.localiserSaisie()
    localiser.addEventListener('click', lancer)
    this.elCoord.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); lancer() }
    })
    this.elEffacer.addEventListener('click', () => this.effacer(true))

    void this.chargerContexte()
  }

  // ── API utilisée par la fenêtre ─────────────────────────────────────────────

  /** Sélectionne la maille `id` (avec sa position si on la connaît). Rend null si inconnue. */
  async selectionner(id: number, position?: { lat: number; lon: number }, source: SourceSelection = 'api'): Promise<Maille | null> {
    this.cadrageAuto = false
    const [trouve] = await Promise.all([this.o.source.parId(id, position), this.typesCharges()])
    if (!trouve) return null
    await this.chargerTuile(trouve.cle)
    return this.appliquer(trouve.entite, source, undefined, true)
  }

  /** Sélectionne la maille qui couvre le point. Rend null hors de la grille. */
  async localiser(lat: number, lon: number): Promise<Maille | null> {
    this.cadrageAuto = false
    this.placerPoint(lon, lat)
    const r = await this.o.source.parPoint(lon, lat)
    if (!r) {
      this.message(this.t('horsGrille', { coord: formaterDecimal(lat, lon) }), true)
      this.map.getView().animate({ center: fromLonLat([lon, lat]), zoom: Math.max(this.map.getView().getZoom() ?? 0, 8), duration: 300 })
      return null
    }
    await Promise.all([...r.cles.map((c) => this.chargerTuile(c)), this.typesCharges()])
    return this.appliquer(r.entite, 'coordonnees', [lon, lat], true)
  }

  effacer(signaler: boolean): void {
    this.point.clear()
    this.elCoord.value = ''
    this.elRecherche.value = ''
    this.message('')
    const avait = this.selection !== null
    this.selection = null
    this.mailles.changed()
    this.afficherInfo()
    if (signaler && avait) this.o.surSelection(null)
  }

  /** À appeler quand la fenêtre s'ouvre : la carte peut avoir eu une taille nulle. */
  rafraichir(): void {
    this.map.updateSize()
  }

  detruire(): void {
    this.observateur?.disconnect()
    this.map.setTarget(undefined)
    this.racine.replaceChildren()
  }

  // ── Construction ────────────────────────────────────────────────────────────

  /** Les couleurs de la carte suivent le thème : lues dans les variables CSS. */
  private construireStyles(): void {
    const cs = getComputedStyle(this.racine)
    const v = (nom: string, defaut: string) => cs.getPropertyValue(nom).trim() || defaut
    const accent = v('--sgi-accent', '#1f6f5c')
    const selection = v('--sgi-selection', '#c2410c')
    const trait = v('--sgi-maille', 'rgba(30,64,120,0.4)')
    const region = v('--sgi-region', 'rgba(71,85,105,0.9)')
    const transparent = new Fill({ color: 'rgba(0,0,0,0)' })   // indispensable au clic sur l'intérieur
    this.styles = {
      maille: new Style({ stroke: new Stroke({ color: trait, width: 0.6 }), fill: transparent }),
      avecSite: new Style({ stroke: new Stroke({ color: trait, width: 0.6 }), fill: new Fill({ color: 'rgba(21,128,61,0.10)' }) }),
      survol: new Style({ stroke: new Stroke({ color: accent, width: 1.6 }), fill: new Fill({ color: 'rgba(30,64,120,0.12)' }), zIndex: 1 }),
      selection: new Style({ stroke: new Stroke({ color: selection, width: 2.6 }), fill: new Fill({ color: translucide(selection, 0.35, 'rgba(234,88,12,0.35)') }), zIndex: 2 }),
      region: new Style({ stroke: new Stroke({ color: region, width: 1.2, lineDash: [6, 4] }) }),
      point: new Style({
        image: new CircleStyle({ radius: 6, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: selection, width: 3 }) }),
        zIndex: 10,
      }),
    }
  }

  private styleSite(type: string): Style {
    let s = this.stylesSite.get(type)
    if (!s) {
      const c = this.couleursSite[type] ?? '#6b7280'
      s = new Style({ stroke: new Stroke({ color: c, width: 1.2 }), fill: new Fill({ color: translucide(c, 0.15, 'rgba(107,114,128,0.15)') }) })
      this.stylesSite.set(type, s)
    }
    return s
  }

  private typeDe(f: FeatureLike): string {
    return String(f.get(PROP_TYPE) ?? '')
  }

  private creerCarte(cible: HTMLElement): void {
    const couches: (TileLayer<XYZ> | VectorLayer)[] = [
      new VectorLayer({ source: this.regions, zIndex: 1, style: this.styles.region }),
      new VectorLayer({ source: this.sites, zIndex: 2, style: (f) => this.styleSite(this.typeDe(f)) }),
      new VectorLayer({
        source: this.mailles,
        zIndex: 3,
        minZoom: this.o.source.enTuiles ? this.o.zoomMailles - 0.001 : undefined,
        style: (f) => this.styleMaille(f),
      }),
      new VectorLayer({ source: this.point, zIndex: 4, style: this.styles.point }),
    ]
    if (this.o.fond) {
      couches.unshift(new TileLayer({
        source: new XYZ({ url: this.o.fond.url, attributions: this.o.fond.attribution, maxZoom: this.o.fond.maxZoom ?? 19, crossOrigin: 'anonymous' }),
        zIndex: 0,
      }))
    }
    const bulle = new Overlay({ element: this.elBulle, offset: [12, 12], positioning: 'top-left', stopEvent: false })
    this.map = new OlMap({
      target: cible,
      layers: couches,
      overlays: [bulle],
      controls: defaultControls({ rotate: false }).extend([new ScaleLine()]),
      view: new View({ maxZoom: 17 }),
    })
    this.cadrer(this.o.source.emprise)

    this.map.on('moveend', () => void this.chargerVue())
    this.map.on('pointerdrag', () => { this.cadrageAuto = false })
    cible.addEventListener('wheel', () => { this.cadrageAuto = false }, { passive: true })
    this.map.on('pointermove', (e) => this.survoler(e, bulle))
    this.map.on('singleclick', (e) => this.cliquer(e))
    cible.addEventListener('pointerleave', () => { this.elBulle.hidden = true; this.definirSurvol(null) })
    if ('ResizeObserver' in window) {
      this.observateur = new ResizeObserver(() => this.map.updateSize())
      this.observateur.observe(cible)
    }
  }

  private cadrer(emprise: readonly number[]): void {
    const taille = this.map.getSize()
    if (!taille || !taille[0] || !taille[1]) return
    this.map.getView().fit(transformExtent(emprise as number[], 'EPSG:4326', 'EPSG:3857'), { padding: [10, 10, 10, 10] })
  }

  /** Régions et sites : facultatifs, et sans effet bloquant s'ils manquent. */
  private async chargerContexte(): Promise<void> {
    const charger = async (url: string | undefined, cible: VectorSource): Promise<Feature<Geometry>[]> => {
      if (!url) return []
      try {
        const fc = await lireJson<object>(this.o.source.adresse(new URL(url, document.baseURI).href))
        const features = this.format.readFeatures(fc) as Feature<Geometry>[]
        cible.addFeatures(features)
        return features
      } catch (e) {
        this.o.surErreur(e as Error)
        return []
      }
    }
    const [regions, charges] = await Promise.all([charger(this.o.regions, this.regions), this.o.sites?.charger()])
    const sites = charges?.features ?? []
    this.sites.addFeatures(sites)
    if (charges) {
      this.filtre = charges.filtre
      this.mailles.changed()   // la teinte des mailles suit les types chargés
    }
    const etendue = this.regions.getExtent()
    if (regions.length && this.cadrageAuto && etendue) this.cadrer(transformExtent(etendue, 'EPSG:3857', 'EPSG:4326'))

    // Légende et couleurs : les types présents dans la couche des sites.
    const types = [...new Set(sites.map((f) => this.typeDe(f)).filter(Boolean))].sort()
    types.forEach((type, i) => {
      this.couleursSite[type] = this.o.typesSite[type]?.couleur || PALETTE[i % PALETTE.length]
    })
    this.stylesSite.clear()
    this.sites.changed()
    this.elLegende.replaceChildren(...types.map((type) => {
      const item = el('span', 'sgi-legende-item')
      const pastille = el('span', 'sgi-pastille')
      pastille.style.background = this.couleursSite[type]
      const libelle = this.o.typesSite[type]?.libelle
      item.append(pastille, document.createTextNode(libelle ? `${type} — ${libelle}` : type))
      return item
    }))

    const nom = this.o.champs.nom, code = this.o.champs.code
    this.elListe.replaceChildren(...sites.map((f) => {
      const o = document.createElement('option')
      o.value = [f.get(nom), f.get(code)].filter((v) => v != null && v !== '').join(' — ')
      return o
    }))
    void this.chargerVue()
  }

  /** Attend les couches de sites : leurs types filtrent `maille.sites`. */
  private async typesCharges(): Promise<void> {
    if (this.o.sites) this.filtre = await this.o.sites.filtre()
  }

  // ── Tuiles ──────────────────────────────────────────────────────────────────

  private async chargerVue(): Promise<void> {
    const zoom = this.map.getView().getZoom() ?? 0
    const visibles = !this.o.source.enTuiles || zoom >= this.o.zoomMailles
    this.elAide.hidden = visibles
    this.elAide.textContent = this.t('zoomer')
    if (!visibles || !this.map.getSize()) return
    const emprise = transformExtent(this.map.getView().calculateExtent(this.map.getSize()), 'EPSG:3857', 'EPSG:4326')
    const cles = this.o.source.clesEmprise([emprise[0] - MARGE_VUE, emprise[1] - MARGE_VUE, emprise[2] + MARGE_VUE, emprise[3] + MARGE_VUE])
    await Promise.all(cles.map((c) => this.chargerTuile(c).catch((e) => this.o.surErreur(e))))
    this.purger(new Set(cles))
  }

  /** Une promesse par tuile : deux demandes simultanées attendent le même chargement. */
  private chargerTuile(cle: string): Promise<void> {
    const deja = this.chargees.get(cle)
    if (deja) {
      this.chargees.delete(cle)          // la tuile redevient la plus récente
      this.chargees.set(cle, deja)
      return Promise.resolve()
    }
    let p = this.enCours.get(cle)
    if (!p) {
      p = this.o.source.tuile(cle).then((entites) => {
        const features = entites.map((e) => {
          const f = this.format.readFeature({ type: 'Feature', id: e.id, geometry: e.geometry, properties: {} }) as Feature
          f.set('_entite', e, true)
          return f
        })
        this.mailles.addFeatures(features)
        this.chargees.set(cle, features)
      }).finally(() => this.enCours.delete(cle))
      this.enCours.set(cle, p)
    }
    return p
  }

  private purger(aGarder: Set<string>): void {
    for (const [cle, features] of [...this.chargees]) {
      if (this.chargees.size <= TUILES_MAX) break
      if (aGarder.has(cle) || cle === '*') continue
      if (this.selection && features.some((f) => f.getId() === this.selection?.id)) continue
      this.mailles.removeFeatures(features)
      this.chargees.delete(cle)
    }
  }

  // ── Interaction ─────────────────────────────────────────────────────────────

  private styleMaille(f: FeatureLike): Style {
    const id = f.getId()
    if (this.selection && id === this.selection.id) return this.styles.selection
    if (id === this.survolId) return this.styles.survol
    const e = f.get('_entite') as EntiteGrille | undefined
    return Array.isArray(e?.properties.sites) && filtrerSites(e.properties.sites, this.filtre).length
      ? this.styles.avecSite
      : this.styles.maille
  }

  private mailleAuPixel(e: MapBrowserEvent): Feature | null {
    let trouvee: Feature | null = null
    this.map.forEachFeatureAtPixel(e.pixel, (f) => { trouvee = f as Feature; return true }, {
      layerFilter: (c) => c.getSource() === this.mailles,
    })
    return trouvee
  }

  private zoomSuffisant(): boolean {
    return !this.o.source.enTuiles || (this.map.getView().getZoom() ?? 0) >= this.o.zoomMailles
  }

  private survoler(e: MapBrowserEvent, bulle: Overlay): void {
    if (e.dragging) return
    const maille = this.zoomSuffisant() ? this.mailleAuPixel(e) : null
    this.definirSurvol(maille ? (maille.getId() as number) : null)
    let texte = ''
    if (maille) {
      const m = versMaille(maille.get('_entite') as EntiteGrille, 'carte', undefined, this.filtre)
      texte = [m.code ?? `#${m.id}`, m.region].filter(Boolean).join(' · ')
    } else {
      const site = this.map.forEachFeatureAtPixel(e.pixel, (f) => f, { layerFilter: (c) => c.getSource() === this.sites })
      if (site) texte = [site.get(this.o.champs.nom), this.typeDe(site)].filter(Boolean).join(' · ')
    }
    this.elBulle.textContent = texte
    this.elBulle.hidden = texte === ''
    bulle.setPosition(texte ? e.coordinate : undefined)
    ;(this.map.getTargetElement() as HTMLElement).style.cursor = maille && !this.o.lectureSeule ? 'pointer' : ''
  }

  private definirSurvol(id: number | null): void {
    if (id === this.survolId) return
    this.survolId = id
    this.mailles.changed()
  }

  private cliquer(e: MapBrowserEvent): void {
    this.cadrageAuto = false
    if (!this.zoomSuffisant()) {
      this.map.getView().animate({ center: e.coordinate, zoom: this.o.zoomMailles + 1, duration: 350 })
      return
    }
    if (this.o.lectureSeule) return
    const f = this.mailleAuPixel(e)
    if (f) void this.typesCharges().then(() => this.appliquer(f.get('_entite') as EntiteGrille, 'carte', undefined, false))
  }

  private appliquer(entite: EntiteGrille, source: SourceSelection, point: [number, number] | undefined, centrer: boolean): Maille {
    const m = versMaille(entite, source, point, this.filtre)
    this.selection = m
    if (source !== 'coordonnees') {
      this.point.clear()
      this.elCoord.value = formaterDecimal(m.centroide[1], m.centroide[0])   // l'autre sens : le centroïde
      this.message('')
    }
    this.elEffacer.disabled = false
    this.mailles.changed()
    this.afficherInfo()
    if (centrer) {
      const f = this.mailles.getFeatureById(m.id) as Feature | null
      const g = f?.getGeometry()
      if (g) this.map.getView().fit(g.getExtent(), { maxZoom: ZOOM_SELECTION + 1, padding: [60, 60, 60, 60], duration: 250 })
    }
    this.o.surSelection(m)
    return m
  }

  private async localiserSaisie(): Promise<void> {
    const lu = lireCoordonnees(this.elCoord.value, this.o.source.emprise as unknown as Emprise)
    if (!lu.ok) {
      this.message(this.t(`err_${lu.erreur}` as never, lu.detail), true)
      return
    }
    const { lat, lon, remarques } = lu.valeur
    this.elCoord.value = formaterDecimal(lat, lon)
    const m = await this.localiser(lat, lon)
    if (m) {
      this.message(remarques.length
        ? this.t('lu', { coord: formaterDecimal(lat, lon), remarques: remarques.map((r) => this.t(`rq_${r}` as never)).join(' ; ') })
        : '')
    }
  }

  private allerAuSite(saisie: string): void {
    const q = saisie.trim().toLowerCase()
    if (!q) return
    const nom = this.o.champs.nom, code = this.o.champs.code
    const tous = this.sites.getFeatures()
    const f = tous.find((s) => [s.get(nom), s.get(code)].filter((v) => v != null && v !== '').join(' — ').toLowerCase() === q)
      ?? tous.find((s) => String(s.get(code) ?? '').toLowerCase() === q || String(s.get(nom) ?? '').toLowerCase().includes(q))
    const g = f?.getGeometry()
    if (g) {
      this.cadrageAuto = false
      this.map.getView().fit(g.getExtent(), { padding: [40, 40, 40, 40], maxZoom: 13, duration: 300 })
    }
  }

  private placerPoint(lon: number, lat: number): void {
    this.point.clear()
    this.point.addFeature(new Feature({ geometry: new Point(fromLonLat([lon, lat])) }))
  }

  private message(texte: string, erreur = false): void {
    this.elMessage.textContent = texte
    this.elMessage.hidden = texte === ''
    this.elMessage.classList.toggle('sgi-erreur', erreur)
  }

  private afficherInfo(): void {
    const s = this.selection
    this.elEffacer.disabled = s === null
    if (!s) {
      this.elInfo.replaceChildren(el('span', 'sgi-discret', this.o.lectureSeule ? this.t('aucuneLecture') : this.t('aucune')))
      return
    }
    const titre = el('div', 'sgi-info-titre')
    titre.append(el('strong', '', this.t('maille', { code: s.code ?? `#${s.id}` })), el('span', 'sgi-id', `id ${s.id}`))
    const lignes: HTMLElement[] = [titre, el('div', '', this.t('region', { region: s.region ?? this.t('enMer') }))]
    if (s.point) lignes.push(el('div', 'sgi-nombre', this.t('pointSaisi', { coord: formaterDecimal(s.point[1], s.point[0]) })))
    lignes.push(el('div', 'sgi-nombre', this.t('centroide', { coord: formaterDecimal(s.centroide[1], s.centroide[0]) })))
    const noms = s.sites.map((x) => (x.nom ?? x.code ?? String(x.id)) + (x.type ? ` (${x.type})` : ''))
    lignes.push(el('div', '', noms.length ? this.t(noms.length > 1 ? 'sites' : 'site', { sites: noms.join(', ') }) : this.t('aucunSite')))
    this.elInfo.replaceChildren(...lignes)
  }
}
